import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, MessageCircle, X } from 'lucide-react';
type NotificationType = 'purchased' | 'chatting';
interface Notification {
  type: NotificationType;
  name: string;
  message: string;
  timeAgo: string;
}
const names = ['María G.', 'José R.', 'Daniela M.', 'Andrés P.', 'Valentina S.', 'Gabriel L.', 'Camila R.', 'Diego F.', 'Isabella T.', 'Sebastián M.', 'Luisa C.', 'Fernando A.', 'Sofía H.', 'Ricardo B.', 'Paola V.', 'Alejandro D.', 'Natalia F.', 'Carlos E.', 'Ana M.', 'Pedro J.', 'Laura B.', 'Miguel Á.', 'Adriana R.', 'Tomás L.', 'Carmen S.', 'Javier P.', 'Mariana D.', 'Óscar F.', 'Claudia T.', 'Emilio V.'];
const venezuelanStates = ['Caracas', 'Miranda', 'Zulia', 'Carabobo', 'Lara', 'Aragua', 'Mérida', 'Táchira', 'Anzoátegui', 'Bolívar', 'Barinas', 'Falcón', 'Sucre', 'Monagas', 'Portuguesa', 'Trujillo', 'Yaracuy', 'Guárico', 'Nueva Esparta', 'Vargas', 'Apure', 'Cojedes', 'Delta Amacuro'];
const timeAgos = ['hace 1 min', 'hace 2 min', 'hace 3 min', 'hace 5 min', 'hace 8 min', 'hace 12 min', 'hace 15 min', 'hace 20 min'];
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function generateNotification(): Notification {
  const types: NotificationType[] = ['purchased', 'chatting'];
  const type = getRandomItem(types);
  const name = getRandomItem(names);
  const state = getRandomItem(venezuelanStates);
  const timeAgo = getRandomItem(timeAgos);
  switch (type) {
    case 'purchased':
      return {
        type,
        name,
        message: `compró el Sistema 7D desde ${state}`,
        timeAgo
      };
    case 'chatting':
      return {
        type,
        name,
        message: `comenzó a chatear por WhatsApp`,
        timeAgo
      };
  }
}
const iconMap = {
  purchased: {
    icon: ShoppingBag,
    bg: 'bg-[#25D366]/15',
    text: 'text-[#25D366]',
    border: 'border-[#25D366]/20'
  },
  chatting: {
    icon: MessageCircle,
    bg: 'bg-orange-500/10',
    text: 'text-orange-500',
    border: 'border-orange-500/20'
  }
};
export function SocialProofToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const showNotification = useCallback(() => {
    if (dismissed) return;
    const notif = generateNotification();
    setNotification(notif);
    setVisible(true);
    setTimeout(() => {
      setVisible(false);
    }, 5000);
  }, [dismissed]);
  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      showNotification();
    }, 8000);
    const interval = setInterval(() => {
      showNotification();
    }, 15000);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [showNotification]);
  if (dismissed) return null;
  const config = notification ? iconMap[notification.type] : null;
  const Icon = config?.icon;
  return <AnimatePresence data-id="element-333">
      {visible && notification && Icon && <motion.div initial={{
      opacity: 0,
      x: -60,
      y: 10
    }} animate={{
      opacity: 1,
      x: 0,
      y: 0
    }} exit={{
      opacity: 0,
      x: -60,
      y: 10
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 300
    }} className="fixed bottom-20 left-2 md:left-4 z-[90] max-w-[260px] md:max-w-[340px]" data-id="element-334">
          <div className={`bg-white rounded-lg md:rounded-xl px-2.5 py-2 md:px-4 md:py-3 shadow-[0_4px_24px_rgba(0,0,0,0.15)] border ${config.border} flex items-start gap-2 md:gap-3 relative group`} data-id="element-335">
            {/* Dismiss */}
            <button onClick={() => {
          setVisible(false);
          setDismissed(true);
        }} className="absolute top-1 right-1 w-4 h-4 md:w-5 md:h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors opacity-0 group-hover:opacity-100" data-id="element-336">
              <X className="w-2.5 h-2.5 md:w-3 md:h-3" data-id="element-337" />
            </button>

            {/* Icon */}
            <div className={`w-7 h-7 md:w-10 md:h-10 rounded-full ${config.bg} ${config.text} flex items-center justify-center shrink-0 mt-0.5`} data-id="element-338">
              <Icon className="w-3.5 h-3.5 md:w-5 md:h-5" data-id="element-339" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pr-3" data-id="element-340">
              <p className="text-[11px] md:text-sm leading-snug" data-id="element-341">
                <span className="text-gray-900 font-semibold" data-id="element-342">
                  {notification.name}
                </span>{' '}
                <span className="text-gray-600" data-id="element-343">{notification.message}</span>
              </p>
              <p className="text-[9px] md:text-[11px] text-gray-400 mt-0.5 md:mt-1 flex items-center gap-1" data-id="element-344">
                <span className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-[#25D366] inline-block animate-pulse" data-id="element-345" />
                {notification.timeAgo}
              </p>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}